/**
 * Thumbnail Scrubber for Admin Video Modal
 *
 * Displays video thumbnails from VTT sprite sheets with a scrub slider.
 * Allows admins to quickly verify thumbnail quality and coverage.
 *
 * Uses deferred loading (via useThumbnailSprite's defer option) to avoid
 * blocking page transitions. The hook waits for browser idle time before
 * fetching VTT data.
 */

import type { ReactElement } from "react";
import { Slider } from "@lumi/ui-headless";
import React, { useCallback, useMemo, useState } from "react";

import { formatTime, useThumbnailSprite } from "../../../../shared/hooks/useThumbnailSprite";

const THUMBNAIL_SCALE = 1.5; // Scale factor for display

export type ThumbnailScrubberProps = Readonly<{
  vttUrl: string;
  duration: number;
}>;

/** Placeholder component for loading/error/empty states */
function ScrubberPlaceholder({ message }: { readonly message: string }): ReactElement {
  return (
    <div
      className="relative bg-neutral-900 rounded-lg overflow-hidden flex items-center justify-center"
      style={{ aspectRatio: "16/9" }}
    >
      <span className="text-xs text-neutral-500">{message}</span>
    </div>
  );
}

/**
 * Thumbnail Scrubber component
 *
 * Uses defer option to load during browser idle time, avoiding blocking
 * page transitions and keeping the UI responsive.
 */
export const ThumbnailScrubber = React.memo(function ThumbnailScrubber({
  vttUrl,
  duration,
}: ThumbnailScrubberProps): ReactElement {
  // Use defer: true to load during idle time, not during page transition
  const { cues, loading, error, preloadedSprites, spriteColumns, findCue, usingFallback } = useThumbnailSprite(
    vttUrl,
    "hd",
    { defer: true },
  );
  const [currentTime, setCurrentTime] = useState(0);

  const currentCue = findCue(currentTime);

  const scaledDimensions = useMemo(() => {
    if (!currentCue) return { width: 640, height: 360, backgroundSize: 0, scale: THUMBNAIL_SCALE };
    const { w, h } = currentCue.coords;
    return {
      width: w * THUMBNAIL_SCALE,
      height: h * THUMBNAIL_SCALE,
      backgroundSize: w * spriteColumns * THUMBNAIL_SCALE,
      scale: THUMBNAIL_SCALE,
    };
  }, [currentCue, spriteColumns]);

  const handleScrub = useCallback((value: number) => setCurrentTime(value), []);

  if (loading) return <ScrubberPlaceholder message="Loading thumbnails..." />;
  if (error) return <ScrubberPlaceholder message="Thumbnails unavailable" />;
  if (!cues.length) return <ScrubberPlaceholder message="No thumbnail data" />;

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <div
          className="relative bg-neutral-950 rounded-lg overflow-hidden mt-2"
          style={{ width: scaledDimensions.width, height: scaledDimensions.height }}
        >
          {currentCue ? (
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url(${currentCue.imageUrl})`,
                backgroundPosition: `-${currentCue.coords.x * scaledDimensions.scale}px -${currentCue.coords.y * scaledDimensions.scale}px`,
                backgroundSize: `${scaledDimensions.backgroundSize}px auto`,
                backgroundRepeat: "no-repeat",
                opacity: preloadedSprites.has(currentCue.imageUrl) ? 1 : 0.5,
              }}
              aria-label={`Thumbnail at ${formatTime(currentTime)}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-neutral-500">No frame</div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 pt-4">
        <span className="text-xs text-neutral-400 tabular-nums min-w-[3.5rem]">{formatTime(currentTime)}</span>
        <div className="flex-1">
          <Slider
            value={Math.floor(currentTime)}
            min={0}
            max={Math.max(0, Math.floor(duration))}
            step={1}
            ariaLabel="Scrub through video thumbnails"
            onChange={handleScrub}
          />
        </div>
        <span className="text-xs text-neutral-400 tabular-nums min-w-[3.5rem] text-right">{formatTime(duration)}</span>
      </div>
      <div className="text-[10px] text-neutral-500 text-center">
        {cues.length} thumbnails • {currentCue ? `Frame ${currentCue.index + 1}` : "—"}
        {usingFallback && " • SD fallback"}
      </div>
    </div>
  );
});
